/**
 * The order of merit.
 *
 * A leaderboard answers "who won on Saturday". This answers "who is
 * having the year", which is what most members are actually playing for
 * and what every club currently keeps in a spreadsheet on one laptop.
 *
 * Four things the screen has to do that a spreadsheet does not.
 *
 * Say what a shared place was worth. Two players tied for second take
 * fourteen each off a 20/16/12 ladder, because between them they hold
 * the second and third places. It looks like an error until it is
 * explained, so it is explained on the row.
 *
 * Say which rounds counted. A member on best-eight of twelve wants the
 * four back, and a screen that cannot show them becomes a phone call.
 *
 * Name the people who are short of qualifying rather than ranking them
 * at the bottom. "Played twice, needs four" and "37th of 38" are
 * different facts and only one of them is true.
 *
 * And say what is NOT counting. There is no automatic linking rule, so
 * the omission has to be visible.
 */

import { useState } from 'react';
import { useApi, useMutation } from '../lib/useApi.ts';
import { api } from '../lib/api.ts';
import { Card, Empty, ErrorBanner, Loading, Status } from './ui.tsx';
import { date as fmtDate } from '../lib/format.ts';
import { FORMAT_LABEL } from './Leaderboard.tsx';

export interface SeriesRow {
  series_id: string;
  name: string;
  starts_on: string;
  ends_on: string;
  scoring: string;
  counts_best: number | null;
  minimum_played: number | null;
  points_beyond: string | number;
  status: string;
  finalised_at: string | null;
  finalised_by_name: string | null;
  notes: string | null;
  competitions: number;
  players: number;
  short_of_qualifying: number;
  not_counting: number;
  ladder_places: number | null;
  created_at: string;
}

export interface StandingRow {
  person_id: string;
  player_name: string;
  played: number;
  counted: number;
  points: string | number;
  best_place: number | null;
  firsts: number;
  seconds: number;
  thirds: number;
  place: number;
  tie_broken_on: string | null;
  is_provisional: boolean;
}

export interface SeriesResult {
  person_id: string;
  player_name: string;
  competition_id: string;
  competition_name: string;
  competition_status: string;
  event_id: string;
  event_name: string;
  play_date: string;
  format: string;
  place: number;
  shared: number;
  field_size: number;
  gross: number;
  net: number;
  stableford: number;
  playing_handicap: number;
  weight: string | number;
  series_points: string | number;
  counted: boolean;
}

interface LinkedRow {
  competition_id: string;
  weight: string | number;
  name: string;
  format: string;
  status: string;
  event_id: string;
  event_name: string;
  play_date: string;
}

interface UnlinkedRow {
  competition_id: string;
  competition_name: string;
  event_id: string;
  event_name: string;
  play_date: string;
  format: string;
  status: string;
  cards_in: number;
}

export interface SeriesPayload {
  series: SeriesRow;
  standings: StandingRow[];
  not_qualified: { person_id: string; player_name: string; played: number; needed: number }[];
  ladder: { place: number; points: string | number }[];
  competitions: LinkedRow[];
  unlinked: UnlinkedRow[];
  is_provisional: boolean;
}

const num = (v: string | number) => Number(v);

/** 20.00 is 20; 3.50 is 3.5. A ladder is read, not audited. */
export function points(v: string | number): string {
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

function placeLabel(place: number, shared: boolean): string {
  const s = place % 10 === 1 && place !== 11 ? 'st'
    : place % 10 === 2 && place !== 12 ? 'nd'
      : place % 10 === 3 && place !== 13 ? 'rd' : 'th';
  return `${shared ? 'T' : ''}${place}${s}`;
}

/** "best eight of twelve", said the way somebody would say it. */
function countingRule(s: SeriesRow): string {
  if (s.scoring === 'stableford') {
    return s.counts_best
      ? `Stableford points, best ${s.counts_best}`
      : 'Stableford points, every round';
  }
  return s.counts_best ? `Best ${s.counts_best} results` : 'Every result counts';
}


/* ------------------------------------------------------------------ */
/* The table                                                           */
/* ------------------------------------------------------------------ */

export function Standings({
  seriesId, canManage = false, reloadKey = 0, onChanged,
}: {
  seriesId: string; canManage?: boolean; reloadKey?: number; onChanged?: () => void;
}) {
  const [reload, setReload] = useState(0);
  const q = useApi<SeriesPayload>(
    `/v1/golf/series/${seriesId}`, [seriesId, reload, reloadKey]);
  const [open, setOpen] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Counting a competition changes what the LIST above says — "3 not
  // counting" is the whole reason that column is there, and a list still
  // saying it after somebody has just counted them is worse than not
  // saying it at all. Found by driving the screen: the panel was right
  // and the row above it was a minute out of date.
  const bump = () => { setReload((n) => n + 1); onChanged?.(); };

  const link = useMutation(async (v: { competition_id: string; weight: number }) => {
    await api(`/v1/golf/series/${seriesId}/competitions`, { method: 'POST', body: v });
    setNote('Counted. The table has moved.');
    bump();
  });

  const unlink = useMutation(async (competitionId: string) => {
    await api(`/v1/golf/series/${seriesId}/competitions/${competitionId}`,
      { method: 'DELETE' });
    setNote('Taken out. It is back on the list of things not counting.');
    bump();
  });

  const declare = useMutation(async (status: string) => {
    await api(`/v1/golf/series/${seriesId}`, { method: 'PATCH', body: { status } });
    setNote(status === 'final'
      ? 'Declared. Reopen it if a card still needs correcting.'
      : 'Reopened.');
    bump();
  });

  const d = q.data;
  if (q.loading && !d) return <Card><Loading /></Card>;
  if (!d) return <Card><ErrorBanner error={q.error} /></Card>;

  const s = d.series;
  const shared = new Map<number, number>();
  for (const r of d.standings) shared.set(r.place, (shared.get(r.place) ?? 0) + 1);

  return (
    <>
      <Card
        title={s.name}
        actions={canManage ? (
          s.status === 'final'
            ? <button className="secondary small" disabled={declare.busy}
                onClick={() => declare.run('open')}>Reopen it</button>
            : <button className="gold small" disabled={declare.busy}
                onClick={() => declare.run('final')}>Declare the season</button>
        ) : undefined}
      >
        {note && <div className="notice">{note}</div>}
        <ErrorBanner error={q.error ?? link.error ?? unlink.error ?? declare.error} />

        <div className="row leader__state">
          {s.status === 'final' ? (
            <Status tone="good">
              Declared {s.finalised_at ? fmtDate(s.finalised_at) : ''}
              {s.finalised_by_name ? ` by ${s.finalised_by_name}` : ''}
            </Status>
          ) : d.is_provisional ? (
            <Status tone="warning">Provisional</Status>
          ) : (
            <Status tone="neutral">Everything in, not yet declared</Status>
          )}
          <span className="muted small">
            {fmtDate(s.starts_on)} – {fmtDate(s.ends_on)} · {countingRule(s)}
            {s.minimum_played ? ` · qualify on ${s.minimum_played}` : ''}
          </span>
        </div>

        {d.standings.length === 0 ? (
          <Empty>
            Nothing counts towards this yet. A season is made of competitions somebody
            linked to it — nothing is added automatically, because the day a club runs
            one that should not count, an automatic rule counts it anyway.
          </Empty>
        ) : (
          <table className="data leader">
            <thead>
              <tr>
                <th className="num">Pos</th>
                <th>Player</th>
                <th className="num">Played</th>
                <th className="num">Counted</th>
                <th className="num">Points</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.standings.map((r) => (
                <tr key={r.person_id}>
                  <td className="num mono">
                    {placeLabel(r.place, (shared.get(r.place) ?? 1) > 1)}
                  </td>
                  <td>
                    {r.player_name}
                    {r.firsts > 0 && (
                      <div className="hint">
                        {r.firsts} win{r.firsts === 1 ? '' : 's'}
                      </div>
                    )}
                  </td>
                  <td className="num">{r.played}</td>
                  <td className="num">
                    {/* Only interesting when it is not all of them. */}
                    {r.counted === r.played ? <span className="muted">all</span> : r.counted}
                  </td>
                  <td className="num"><strong>{points(r.points)}</strong></td>
                  <td className="small">
                    {r.tie_broken_on && r.tie_broken_on !== 'nothing — a genuine tie' && (
                      <span className="muted">behind on {r.tie_broken_on}</span>
                    )}
                    {r.tie_broken_on === 'nothing — a genuine tie' && (
                      <span className="muted">tied outright</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {d.not_qualified.length > 0 && (
          <>
            <h3 className="small" style={{ marginTop: 16 }}>Not yet qualified</h3>
            <p className="hint">
              {/* GOLF-20. Ranking somebody 37th of 38 when they have played
                  twice tells them something untrue at exactly the moment
                  they were deciding whether to enter again. */}
              {d.not_qualified.map((n) => (
                `${n.player_name} (${n.played} of ${n.needed})`
              )).join(', ')}.
            </p>
          </>
        )}
      </Card>

      {canManage && (
        <Card title="What Counts">
          <p className="muted small">
            Nothing is added automatically. A competition counts because somebody said
            so — which is the only way a club can run a charity day that should not.
          </p>

          {d.competitions.length === 0 ? (
            <Empty>Nothing linked yet.</Empty>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Day</th><th>Competition</th><th className="num">Weight</th>
                  <th>State</th><th />
                </tr>
              </thead>
              <tbody>
                {d.competitions.map((c) => (
                  <tr key={c.competition_id}>
                    <td>
                      {fmtDate(c.play_date)}
                      <div className="hint">{c.event_name}</div>
                    </td>
                    <td>
                      {c.name}
                      <div className="hint">{FORMAT_LABEL[c.format] ?? c.format}</div>
                    </td>
                    <td className="num">
                      {num(c.weight) === 1
                        ? <span className="muted">—</span>
                        : <strong>×{points(c.weight)}</strong>}
                    </td>
                    <td className="small">
                      {c.status === 'final'
                        ? <Status tone="good">Declared</Status>
                        : c.status === 'abandoned'
                          ? <span className="muted">Abandoned — scores nothing</span>
                          : <Status tone="warning">Provisional</Status>}
                    </td>
                    <td className="actions">
                      {s.status !== 'final' && (
                        <>
                          <button className="secondary small" disabled={link.busy}
                            onClick={() => link.run({
                              competition_id: c.competition_id,
                              weight: num(c.weight) === 1 ? 2 : 1,
                            })}>
                            {num(c.weight) === 1 ? 'Count it double' : 'Back to single'}
                          </button>
                          <button className="secondary small" disabled={unlink.busy}
                            onClick={() => unlink.run(c.competition_id)}>Take it out</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {d.unlinked.length > 0 && (
            <>
              <h3 className="small" style={{ marginTop: 20 }}>
                Played inside the season and not counting
              </h3>
              <p className="hint">
                {/* The nudge that replaces an automatic rule. Forgetting to
                    link is the likelier mistake, so it is on the screen. */}
                Either these were not meant to count, or somebody forgot.
              </p>
              <table className="data">
                <thead>
                  <tr>
                    <th>Day</th><th>Competition</th>
                    <th className="num">Cards</th><th />
                  </tr>
                </thead>
                <tbody>
                  {d.unlinked.map((u) => (
                    <tr key={u.competition_id}>
                      <td>
                        {fmtDate(u.play_date)}
                        <div className="hint">{u.event_name}</div>
                      </td>
                      <td>
                        {u.competition_name}
                        <div className="hint">{FORMAT_LABEL[u.format] ?? u.format}</div>
                      </td>
                      <td className="num">{u.cards_in}</td>
                      <td className="actions">
                        <button className="gold small" disabled={link.busy}
                          onClick={() => link.run({
                            competition_id: u.competition_id, weight: 1,
                          })}>Count it</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Card>
      )}

      {canManage && d.standings.length > 0 && (
        <Card title="One Player's Season">
          <p className="muted small">
            The call the shop gets: &ldquo;why does it say six when I played nine?&rdquo;
          </p>
          <div className="row">
            {d.standings.map((r) => (
              <button key={r.person_id}
                className={open === r.person_id ? 'gold small' : 'secondary small'}
                onClick={() => setOpen(open === r.person_id ? null : r.person_id)}>
                {r.player_name}
              </button>
            ))}
          </div>
          {open && <SeriesResults seriesId={seriesId} personId={open} reloadKey={reload} />}
        </Card>
      )}
    </>
  );
}


/* ------------------------------------------------------------------ */
/* One player's season, round by round                                 */
/* ------------------------------------------------------------------ */

export function SeriesResults({
  seriesId, personId, reloadKey = 0,
}: { seriesId: string; personId?: string; reloadKey?: number }) {
  const url = `/v1/golf/series/${seriesId}/results${
    personId ? `?person_id=${personId}` : ''}`;
  const q = useApi<{ results: SeriesResult[] }>(url, [seriesId, personId, reloadKey]);
  const rows = q.data?.results ?? [];

  if (q.loading) return <Loading />;
  if (rows.length === 0) return <Empty>No rounds in this one yet.</Empty>;

  const dropped = rows.filter((r) => !r.counted).length;

  return (
    <>
      <table className="data" style={{ marginTop: 12 }}>
        <thead>
          <tr>
            <th>Day</th><th>Competition</th>
            <th className="num">Place</th><th className="num">Points</th><th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.competition_id}
              className={r.counted ? undefined : 'leader__dropped'}>
              <td>{fmtDate(r.play_date)}</td>
              <td>
                {r.event_name}
                <div className="hint">
                  {r.competition_name} · {FORMAT_LABEL[r.format] ?? r.format}
                  {num(r.weight) !== 1 && ` · counts ×${points(r.weight)}`}
                </div>
              </td>
              <td className="num mono">
                {placeLabel(r.place, r.shared > 1)}
                <div className="hint">of {r.field_size}</div>
              </td>
              <td className="num">
                <strong>{points(r.series_points)}</strong>
                {/* The number that looks like an error until it is
                    explained: a shared place is worth the average of the
                    places it covers. */}
                {r.shared > 1 && (
                  <div className="hint">
                    {r.shared} shared {r.place}
                    {r.place === 1 ? 'st' : r.place === 2 ? 'nd' : r.place === 3 ? 'rd' : 'th'}
                  </div>
                )}
              </td>
              <td className="small">
                {r.counted
                  ? null
                  : <span className="muted">not counted</span>}
                {r.competition_status !== 'final' && (
                  <div className="hint">provisional</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {dropped > 0 && (
        <p className="hint">
          {dropped} round{dropped === 1 ? '' : 's'} did not count towards the total —
          the season counts the best few, and these were not among them.
        </p>
      )}
    </>
  );
}


/* ------------------------------------------------------------------ */
/* Where a member stands                                               */
/* ------------------------------------------------------------------ */

interface MyStanding extends StandingRow {
  series_id: string;
  name: string;
  scoring: string;
  counts_best: number | null;
  minimum_played: number | null;
  status: string;
  starts_on: string;
  ends_on: string;
  field_size: number;
}

interface ShortRow {
  series_id: string;
  name: string;
  status: string;
  played: number;
  needed: number;
}

/**
 * A member's own line in every season they are in.
 *
 * They should not have to open three tables and read down each one
 * looking for their own name — and the member who is short of
 * qualifying, who appears on none of them, is told what they are short
 * of rather than nothing at all.
 */
export function MyStandings() {
  const q = useApi<{ standings: MyStanding[]; short_of_qualifying: ShortRow[] }>(
    '/v1/golf/standings');
  const [open, setOpen] = useState<string | null>(null);

  const rows = q.data?.standings ?? [];
  const short = q.data?.short_of_qualifying ?? [];
  if (q.loading || (rows.length === 0 && short.length === 0)) return null;

  return (
    <Card title="Where You Stand">
      <ErrorBanner error={q.error} />

      {rows.length > 0 && (
        <table className="data">
          <thead>
            <tr>
              <th>Season</th>
              <th className="num">Place</th>
              <th className="num">Points</th>
              <th className="num">Counted</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.series_id}>
                <td>
                  {r.name}
                  <div className="hint">
                    {r.counts_best ? `best ${r.counts_best}` : 'every round counts'}
                  </div>
                </td>
                <td className="num mono">
                  {placeLabel(r.place, false)}
                  <div className="hint">of {r.field_size}</div>
                </td>
                <td className="num"><strong>{points(r.points)}</strong></td>
                <td className="num">
                  {r.counted} of {r.played}
                  {r.counted < r.played && (
                    <div className="hint">
                      <button className="secondary small"
                        onClick={() => setOpen(open === r.series_id ? null : r.series_id)}>
                        {open === r.series_id ? 'hide' : 'which ones?'}
                      </button>
                    </div>
                  )}
                </td>
                <td className="small">
                  {r.status !== 'final' && <Status tone="warning">Provisional</Status>}
                  {r.tie_broken_on && r.tie_broken_on !== 'nothing — a genuine tie' && (
                    <div className="hint">behind on {r.tie_broken_on}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && <SeriesResults seriesId={open} />}

      {short.map((s) => (
        <p key={s.series_id} className="hint">
          {/* GOLF-20 carried through to the person it is about. */}
          {s.name}: you have played {s.played} of the {s.needed} needed to appear on
          the table.
        </p>
      ))}
    </Card>
  );
}
